import React, { useState } from "react";

/**
 * Lista de productos escaneados (rediseño 2026-07): tarjetas con stepper de
 * cantidad, indicador de stock (en salidas) y barra de acción fija.
 *
 * @param {Object} props
 * @param {Array} props.processedItems - Productos procesados
 * @param {string} props.mode - 'add' | 'take'
 * @param {Function} props.editItemQuantity - Fija la cantidad absoluta (codigo, valor)
 * @param {Function} props.decrementItemQuantity - Baja 1 la cantidad (codigo)
 * @param {Function} props.submitInventoryData - Envía el movimiento
 * @param {boolean} props.isLoading - Operación en curso
 * @param {Array} props.scannedCodes - Códigos escaneados originales
 */
const ScannedItemsTable = ({
  processedItems,
  mode,
  editItemQuantity,
  decrementItemQuantity,
  submitInventoryData,
  isLoading,
  scannedCodes, // eslint-disable-line no-unused-vars
}) => {
  const hasItems = processedItems.length > 0;
  const isTake = mode === "take";

  // Valores temporales de los inputs mientras se editan (misma lógica previa).
  const [tempValues, setTempValues] = useState({});

  const handleQuantityChange = (e, codigo) => {
    setTempValues((prev) => ({ ...prev, [codigo]: e.target.value }));
  };

  const handleQuantityKeyDown = (e, codigo) => {
    e.stopPropagation();
    if (e.key === "Enter") {
      handleQuantityBlur(e, codigo);
      e.target.blur();
    }
  };

  const handleQuantityFocus = (codigo) => {
    const currentItem = processedItems.find((item) => item.codigo === codigo);
    setTempValues((prev) => ({
      ...prev,
      [codigo]: currentItem?.cantidad?.toString() || "",
    }));
  };

  const handleQuantityBlur = (e, codigo) => {
    const value = e.target.value.trim();
    setTempValues((prev) => {
      const next = { ...prev };
      delete next[codigo];
      return next;
    });
    if (value === "" || isNaN(parseInt(value, 10)) || parseInt(value, 10) <= 0) {
      return;
    }
    const numericValue = parseInt(value, 10);
    if (numericValue > 0) editItemQuantity(codigo, numericValue);
  };

  const getInputValue = (item) => {
    if (tempValues[item.codigo] !== undefined) return tempValues[item.codigo];
    return item.cantidad || "";
  };

  // Bloqueante = error que NO se puede corregir ajustando la cantidad.
  const isBlocked = (item) => !!item.error && item.error !== "insufficient_stock";

  const canIncrement = (item) => {
    if (isBlocked(item)) return false;
    if (isTake && item.stock != null) return item.cantidad < item.stock;
    return true;
  };

  const totalUnidades = processedItems.reduce(
    (sum, item) => sum + (Number(item.cantidad) || 0),
    0,
  );

  return (
    <section className="scanner-table-container scanned-panel">
      <div className="scanned-panel-head">
        <h2 className="scanned-panel-title">Productos escaneados</h2>
        <span className="scanned-count-badge">{processedItems.length}</span>
      </div>

      <div className={`scanned-list-scroll ${hasItems ? "has-items" : "is-empty"}`}>
        {hasItems ? (
          <ul className="scanned-list">
            {processedItems.map((item, index) => (
              <li
                key={item.codigo || index}
                className={`scanned-card ${item.error ? "is-error" : ""}`}
              >
                <div className="scanned-card-info">
                  <span className="scanned-card-name">
                    {item.descripcion || "Producto sin nombre"}
                  </span>
                  <div className="scanned-card-tags">
                    <span className="scanned-tag">
                      <span className="scanned-tag-key">Medida</span>
                      {item.medida && item.medida !== "-"
                        ? item.medida
                        : "N/A"}
                    </span>
                    <span className="scanned-tag">
                      <span className="scanned-tag-key">Tipo</span>
                      {item.tipo && item.tipo !== "-" ? item.tipo : "N/A"}
                    </span>
                  </div>
                  {item.codigo && (
                    <span className="scanned-card-code">{item.codigo}</span>
                  )}
                  {item.error && (
                    <span className="scanned-card-error">
                      <i className="fa-solid fa-triangle-exclamation"></i>
                      {item.mensaje}
                    </span>
                  )}
                </div>

                <div className="scanned-card-controls-row">
                  {isTake && (
                    <div className="scanned-card-stock" title="Stock disponible">
                      <span className="scanned-stock-label">Stock</span>
                      <span className="scanned-stock-value">{item.stock}</span>
                    </div>
                  )}

                  <div className="scanned-controls">
                    <div className="scanned-stepper">
                      <button
                        type="button"
                        className="scanned-step-btn"
                        onClick={() => decrementItemQuantity(item.codigo)}
                        disabled={item.cantidad <= 1 || isBlocked(item)}
                        aria-label="Disminuir cantidad"
                      >
                        <i className="fa-solid fa-minus"></i>
                      </button>
                      <input
                        type="number"
                        className={`scanned-qty-input ${
                          item.error === "insufficient_stock" ? "is-invalid" : ""
                        }`}
                        value={getInputValue(item)}
                        onChange={(e) => handleQuantityChange(e, item.codigo)}
                        onKeyDown={(e) => handleQuantityKeyDown(e, item.codigo)}
                        onFocus={() => handleQuantityFocus(item.codigo)}
                        onBlur={(e) => handleQuantityBlur(e, item.codigo)}
                        min="1"
                        max={isTake ? item.stock : undefined}
                        placeholder="0"
                        disabled={isBlocked(item)}
                        aria-label="Cantidad"
                      />
                      <button
                        type="button"
                        className="scanned-step-btn"
                        onClick={() =>
                          editItemQuantity(
                            item.codigo,
                            (Number(item.cantidad) || 0) + 1,
                          )
                        }
                        disabled={!canIncrement(item)}
                        aria-label="Aumentar cantidad"
                      >
                        <i className="fa-solid fa-plus"></i>
                      </button>
                    </div>
                    <button
                      type="button"
                      className="scanned-remove"
                      onClick={() => editItemQuantity(item.codigo, 0)}
                      aria-label="Eliminar producto"
                      title="Eliminar producto"
                    >
                      <i className="fa-solid fa-trash-can"></i>
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="scanned-empty" role="status" aria-live="polite">
            <span className="scanned-empty-icon">
              <i className="fa-solid fa-barcode"></i>
            </span>
            <p className="scanned-empty-title">Aún no has escaneado nada</p>
            <small className="scanned-empty-sub">
              Los productos que escanees aparecerán aquí listos para confirmar.
            </small>
          </div>
        )}
      </div>

      {hasItems && (
        <div className="scanned-actionbar">
          <div className="scanned-totals">
            <div className="scanned-total">
              <span>Productos</span>
              <strong>{processedItems.length}</strong>
            </div>
            <div className="scanned-total">
              <span>Unidades</span>
              <strong>{totalUnidades}</strong>
            </div>
          </div>
          <button
            className={`scanned-confirm ${mode === "add" ? "add" : "take"}`}
            onClick={submitInventoryData}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="scanned-spinner"></span> Procesando…
              </>
            ) : mode === "add" ? (
              <>
                <i className="fa-solid fa-plus"></i> Confirmar entrada
              </>
            ) : (
              <>
                <i className="fa-solid fa-minus"></i> Confirmar salida
              </>
            )}
          </button>
        </div>
      )}
    </section>
  );
};

export default React.memo(ScannedItemsTable);
